// Lazy singleton canvas context used for off-screen text measurement.
// The IIFE runs once to set up private `ctx` state; the returned function
// initialises the canvas on first call and reuses it thereafter.
const getMeasureCtx = (() => {
  let ctx: CanvasRenderingContext2D | null = null;
  return (): CanvasRenderingContext2D | null => {
    if (!ctx && typeof document !== 'undefined') {
      ctx = document.createElement('canvas').getContext('2d');
    }
    return ctx;
  };
})();

export const measureTextWidth = (text: string, font: string): number => {
  const ctx = getMeasureCtx();
  if (!ctx) {
    return text.length * 8;
  }
  ctx.font = font;
  return ctx.measureText(text).width;
};

export const wrapText = (text: string, maxWidth: number, font: string): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';
  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    if (measureTextWidth(testLine, font) > maxWidth && currentLine) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) {
    lines.push(currentLine);
  }
  return lines.length > 0 ? lines : [text];
};

export const wrapTextWithHyphenation = (
  text: string,
  maxWidth: number,
  font: string,
  maxLines: number
): { lines: string[]; isTruncated: boolean } => {
  // No hyphenation if the text already fits — hyphenation only to save lines
  const rawLines = wrapText(text, maxWidth, font);
  if (rawLines.length <= maxLines) {
    return { lines: rawLines, isTruncated: false };
  }

  // Greedy line-aware hyphenation: when a word doesn't fit at the end of a line,
  // try to fill the line by hyphenating the word rather than pushing it whole.
  const lines: string[] = [];
  let currentLine = '';
  const segments = text.split(' ');

  for (let i = 0; i < segments.length;) {
    const word = segments[i];
    const candidate = currentLine ? `${currentLine} ${word}` : word;

    if (measureTextWidth(candidate, font) <= maxWidth) {
      currentLine = candidate;
      i++;
    } else if (!currentLine) {
      // Word alone exceeds maxWidth — force-break it
      let splitAt = word.length - 1;
      while (splitAt > 1 && measureTextWidth(`${word.slice(0, splitAt)}-`, font) > maxWidth) {
        splitAt--;
      }
      // splitAt <= 1 means even a 1-char prefix + hyphen exceeds maxWidth.
      // Emit the word as-is and let CSS overflow-wrap handle the visual break.
      if (splitAt <= 1) {
        lines.push(word);
        i++;
      } else {
        lines.push(`${word.slice(0, splitAt)}-`);
        segments[i] = word.slice(splitAt);
      }
    } else {
      // Try to fit a prefix of the word on the current line (line-aware hyphenation)
      const usedWidth = measureTextWidth(currentLine, font);
      const spaceWidth = measureTextWidth(' ', font);
      let prefixLen = 0;
      for (let j = word.length; j >= 2; j--) {
        if (usedWidth + spaceWidth + measureTextWidth(`${word.slice(0, j)}-`, font) <= maxWidth) {
          prefixLen = j;
          break;
        }
      }

      if (prefixLen >= 2) {
        lines.push(`${currentLine} ${word.slice(0, prefixLen)}-`);
        currentLine = '';
        segments[i] = word.slice(prefixLen);
      } else {
        lines.push(currentLine);
        currentLine = '';
        // Don't increment — retry this word on the now-empty line
      }
    }
  }

  if (currentLine) {
    lines.push(currentLine);
  }

  if (lines.length <= maxLines) {
    return { lines, isTruncated: false };
  }

  const truncated = lines.slice(0, maxLines);
  let lastLine = truncated[maxLines - 1];
  // Keep at least 1 char so we never emit a bare "..."
  while (lastLine.length > 1 && measureTextWidth(`${lastLine}...`, font) > maxWidth) {
    lastLine = lastLine.slice(0, -1).trimEnd();
  }
  truncated[maxLines - 1] = `${lastLine}...`;
  return { lines: truncated, isTruncated: true };
};
